"use client";

import React from "react";
import {
  Eye,
  ListMinus,
  ListPlus,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import {
  type AddCollectionCardResponse,
  type CollectionCard,
  type DeleteBulkCollectionCardsResponse,
} from "modules/collections/details/model/collection";
import { useCollection } from "modules/collections/details/hooks/useCollection";
import { useConfirmDeleteModal } from "modules/common/stores/useConfirmDeleteStore";
import { useCardActions } from "modules/common/components/actions";
import { revalidateCollectionCaches } from "modules/server/actions/collection";
import { revalidateTradeListCaches } from "modules/server/actions/list";
import { QUERIES } from "modules/common/query";
import {
  useMultiSelectStore,
  getSelectionState,
} from "modules/common/stores/useMultiSelectStore";
import { Checkbox } from "modules/common/components/ui/Checkbox";
import { ActionDefinition } from "modules/common/components/actions/useCardActions";
import { selectableInventoryIds } from "modules/common/selectableInventoryIds";
import type { BinderItemsRequest } from "modules/binder/model/binder";
import {
  applyOptimisticBinderCollectionQuantityAdd,
  applyOptimisticBinderCollectionQuantityRemove,
} from "modules/binder/optimisticAdd";
import {
  collectionCardPendingAddKey,
  useCollectionCardPendingStore,
} from "modules/collections/stores/useCollectionCardPendingStore";
import { invalidateInventoryFacetQueries } from "modules/common/invalidateInventoryQueries";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { useToast } from "modules/common/components/toast/useToast";
import { API_ROUTES } from "utils/api-route.constants";
import { extractErrorMessage } from "utils/error-handling";

const BINDER_COLLECTION_MUTATION_KEY = [
  "binder",
  "collection-inventory",
] as const;
const BINDER_RECONCILIATION_DELAY_MS = 5_000;

interface BinderReconciliationBatch {
  collectionId: string;
  listIds: Set<string>;
  timeout?: ReturnType<typeof setTimeout>;
}

const binderReconciliationBatches = new WeakMap<
  QueryClient,
  BinderReconciliationBatch
>();

function beginBinderReconciliationBatch(
  queryClient: QueryClient,
  collectionId: string
) {
  const batch = binderReconciliationBatches.get(queryClient) ?? {
    collectionId,
    listIds: new Set<string>(),
  };
  if (batch.timeout) clearTimeout(batch.timeout);
  batch.timeout = undefined;
  batch.collectionId = collectionId;
  binderReconciliationBatches.set(queryClient, batch);
}

function addBinderReconciliationLists(
  queryClient: QueryClient,
  listIds: readonly string[] | undefined
) {
  const batch = binderReconciliationBatches.get(queryClient);
  if (!batch) return;
  for (const listId of listIds ?? []) {
    if (listId) batch.listIds.add(listId);
  }
}

function scheduleBinderReconciliation(
  queryClient: QueryClient,
  reconcile: (collectionId: string, listIds: string[]) => Promise<void>
) {
  const batch = binderReconciliationBatches.get(queryClient);
  if (!batch) return;
  if (batch.timeout) clearTimeout(batch.timeout);

  batch.timeout = setTimeout(() => {
    if (
      queryClient.isMutating({
        mutationKey: BINDER_COLLECTION_MUTATION_KEY,
        exact: true,
      }) > 0
    ) {
      scheduleBinderReconciliation(queryClient, reconcile);
      return;
    }

    binderReconciliationBatches.delete(queryClient);
    void reconcile(batch.collectionId, [...batch.listIds]);
  }, BINDER_RECONCILIATION_DELAY_MS);
}

interface UseCollectionActionsProps {
  card: CollectionCard;
  binderRequest?: BinderItemsRequest;
  disabled?: boolean;
  onAddToTradeList?: () => void;
  onRemoveFromTradeList?: () => void;
  onViewCardDetails?: () => void;
  onEditCard?: () => void;
  enableSelect?: boolean;
}

export function useCollectionActions({
  card,
  binderRequest,
  disabled = false,
  onAddToTradeList,
  onRemoveFromTradeList,
  onViewCardDetails,
  onEditCard,
  enableSelect = true,
}: UseCollectionActionsProps) {
  const { collection } = useCollection();
  const { openModal, closeModal } = useConfirmDeleteModal();
  const queryClient = useQueryClient();
  const client = useFetchWrapper();
  const toast = useToast();
  const availabilityPending =
    Boolean(binderRequest) &&
    Boolean(card.tradeItem) &&
    card.availableIds === undefined;
  const selectableIds = availabilityPending ? [] : selectableInventoryIds(card);
  const selectionState = useMultiSelectStore((state) =>
    getSelectionState(state.selectedIds, selectableIds)
  );
  const toggleItem = useMultiSelectStore((state) => state.toggleItem);
  const hasReservedCopies = (card.spokenForQuantity ?? 0) > 0;
  const hasUnresolvedCopyIds = card.quantity !== card.ids.length;
  const hasOptimisticCopyIds = card.ids.some((id) =>
    id.startsWith("binder-optimistic:")
  );
  const pendingAddKey = collectionCardPendingAddKey(card);
  const isAddFlowPending = useCollectionCardPendingStore((state) =>
    state.pendingAddKeys.has(pendingAddKey)
  );
  const beginPendingAdd = useCollectionCardPendingStore(
    (state) => state.beginAdd
  );
  const endPendingAdd = useCollectionCardPendingStore((state) => state.endAdd);

  const shouldReconcileAfterMutation = () =>
    !binderRequest ||
    queryClient.isMutating({
      mutationKey: BINDER_COLLECTION_MUTATION_KEY,
      exact: true,
    }) === 0;

  const reconcileAfterMutation = async (
    collectionId: string,
    listIds: string[]
  ) => {
    await Promise.allSettled([
      revalidateCollectionCaches(collectionId),
      listIds.length > 0
        ? revalidateTradeListCaches(listIds)
        : Promise.resolve(),
    ]);
    await Promise.allSettled([
      invalidateInventoryFacetQueries(queryClient, "collection", "trade"),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.COLLECTION_CARDS],
      }),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.CARD_OWNERSHIP_STATUS],
      }),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.TRADE_LIST_ITEMS],
      }),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.TRADE_LISTS_PAGED],
      }),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.TRADE_LISTS_USER],
      }),
      ...listIds.map((listId) =>
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_LISTS_DETAILS, listId],
        })
      ),
    ]);
  };

  const settleMutation = async (listIds?: readonly string[]) => {
    if (!collection) return;
    if (!binderRequest) {
      await reconcileAfterMutation(collection.id, [...(listIds ?? [])]);
      return;
    }
    addBinderReconciliationLists(queryClient, listIds);
    if (shouldReconcileAfterMutation()) {
      scheduleBinderReconciliation(queryClient, reconcileAfterMutation);
    }
  };

  const addOneMutation = useMutation({
    mutationKey: binderRequest ? BINDER_COLLECTION_MUTATION_KEY : undefined,
    mutationFn: (): Promise<AddCollectionCardResponse> => {
      if (!collection) throw new Error("Collection not found");
      return client.post(API_ROUTES.COLLECTIONS.ADD_CARD(collection.id), {
        collectionId: collection.id,
        cardId: card.dataId,
        finishes: card.finishes,
        language: card.language,
        condition: card.condition,
        quantity: 1,
        purchasePrice: card.purchasePrice ?? 0,
      });
    },
  });

  const deleteCopiesMutation = useMutation({
    mutationKey: binderRequest ? BINDER_COLLECTION_MUTATION_KEY : undefined,
    mutationFn: ({
      ids,
      remainingIds,
    }: {
      ids: string[];
      remainingIds?: string[];
    }): Promise<DeleteBulkCollectionCardsResponse> => {
      if (!collection) throw new Error("Collection not found");
      return client.delete(
        API_ROUTES.COLLECTIONS.DELETE_BULK_CARDS(collection.id),
        { ids, remainingIds }
      );
    },
  });

  const isAddingOne = addOneMutation.isPending;
  const isDeleting = deleteCopiesMutation.isPending;

  const handleDeleteCard = () => {
    if (!collection) return;
    openModal({
      name: card.name,
      loadingButton: !binderRequest,
      confirmFunc: async () => {
        if (binderRequest) {
          closeModal();
          beginBinderReconciliationBatch(queryClient, collection.id);
        }
        const optimistic = binderRequest
          ? await applyOptimisticBinderCollectionQuantityRemove(queryClient, {
              request: binderRequest,
              card,
              removedIds: card.ids,
            })
          : undefined;
        try {
          const result = await deleteCopiesMutation.mutateAsync({
            ids: card.ids,
          });
          toast.success("Card deleted");
          await settleMutation(result.affectedListIds);
        } catch (error) {
          optimistic?.rollback();
          toast.error(extractErrorMessage(error, "Failed to delete the card"));
          await settleMutation();
        }
      },
    });
  };

  const handleAddOne = async () => {
    if (!collection) return;
    if (
      useCollectionCardPendingStore.getState().pendingAddKeys.has(pendingAddKey)
    ) {
      return;
    }
    beginPendingAdd(pendingAddKey);
    if (binderRequest) {
      beginBinderReconciliationBatch(queryClient, collection.id);
    }

    try {
      const optimistic = binderRequest
        ? await applyOptimisticBinderCollectionQuantityAdd(queryClient, {
            request: binderRequest,
            card,
          })
        : undefined;
      try {
        const result = await addOneMutation.mutateAsync();
        optimistic?.commitCreatedId(result.card.id);
        toast.success("Card added");
        await settleMutation(result.updatedLists?.map((list) => list.id));
      } catch (error) {
        optimistic?.rollback();
        toast.error(extractErrorMessage(error, "Failed to add the card"));
        await settleMutation();
      }
    } finally {
      endPendingAdd(pendingAddKey);
    }
  };

  const handleRemoveOne = async () => {
    if (!collection) return;
    const cardId = selectableIds[0];
    if (!cardId) return;
    const remainingIds = card.ids.filter((id) => id !== cardId);
    if (binderRequest) {
      beginBinderReconciliationBatch(queryClient, collection.id);
    }
    const optimistic = binderRequest
      ? await applyOptimisticBinderCollectionQuantityRemove(queryClient, {
          request: binderRequest,
          card,
          removedIds: [cardId],
        })
      : undefined;
    try {
      const result = await deleteCopiesMutation.mutateAsync({
        ids: [cardId],
        remainingIds,
      });
      toast.success("Card removed");
      await settleMutation(result.affectedListIds);
    } catch (error) {
      optimistic?.rollback();
      toast.error(extractErrorMessage(error, "Failed to remove the card"));
      await settleMutation();
    }
  };

  const handleSelectCard = () => {
    if (selectableIds.length > 0 && !hasUnresolvedCopyIds) {
      toggleItem(selectableIds);
    }
  };

  const actionDefinitions: ActionDefinition[] = [
    {
      id: "add-to-trade-list",
      label: "Add to List",
      icon: <ListPlus size={ICON_SIZE} />,
      handler: onAddToTradeList || (() => {}),
      disabled: disabled || !onAddToTradeList,
      variant: "primary",
      condition:
        !card.tradeItem && !hasOptimisticCopyIds && !hasUnresolvedCopyIds,
    },
    {
      id: "remove-from-trade-list",
      label: "Remove from List",
      icon: <ListMinus size={ICON_SIZE} />,
      handler: onRemoveFromTradeList || (() => {}),
      disabled: disabled || !onRemoveFromTradeList,
      variant: "error",
      condition:
        !!card.tradeItem &&
        !hasOptimisticCopyIds &&
        selectableIds.length > 0 &&
        !hasUnresolvedCopyIds,
    },
    {
      id: "view-details",
      label: "View Details",
      icon: <Eye size={ICON_SIZE} />,
      handler: onViewCardDetails || (() => {}),
      disabled: disabled || !onViewCardDetails,
      variant: "secondary",
      condition: !!onViewCardDetails,
    },
    {
      id: "edit-card",
      label: "Edit Card",
      icon: <Pencil size={ICON_SIZE} />,
      handler: onEditCard || (() => {}),
      disabled: disabled || !onEditCard,
      variant: "secondary",
      condition:
        !!onEditCard &&
        !hasOptimisticCopyIds &&
        selectableIds.length > 0 &&
        !hasUnresolvedCopyIds,
    },
    {
      id: "add-one",
      label: "Add one",
      icon:
        isAddingOne || isAddFlowPending ? (
          <Loader2 size={ICON_SIZE} className="animate-spin" />
        ) : (
          <Plus size={ICON_SIZE} />
        ),
      handler: handleAddOne,
      disabled: disabled || !collection || isAddingOne || isAddFlowPending,
      loading: isAddingOne || isAddFlowPending,
      variant: "secondary",
    },
    {
      id: "remove-one",
      label: "Remove one",
      icon: isDeleting ? (
        <Loader2 size={ICON_SIZE} className="animate-spin" />
      ) : (
        <Minus size={ICON_SIZE} />
      ),
      handler: handleRemoveOne,
      disabled: disabled || isDeleting,
      loading: isDeleting,
      variant: "secondary",
      condition:
        !hasUnresolvedCopyIds &&
        !hasOptimisticCopyIds &&
        card.ids.length > 1 &&
        selectableIds.length > 0,
    },
    {
      id: "select",
      label: "Select Card",
      icon: (
        <Checkbox
          checked={selectionState === "all"}
          indeterminate={selectionState === "partial"}
          size="xs"
          className="pointer-events-none p-0"
          onChange={() => {}}
          aria-label={`Select item ${card.dataId}`}
        />
      ),
      handler: handleSelectCard,
      disabled,
      variant: "secondary",
      condition:
        enableSelect &&
        !hasOptimisticCopyIds &&
        selectableIds.length > 0 &&
        !hasUnresolvedCopyIds,
    },
    {
      id: "delete",
      label: "Delete Card",
      icon: isDeleting ? (
        <Loader2 size={ICON_SIZE} className="animate-spin" />
      ) : (
        <Trash2 size={ICON_SIZE} />
      ),
      handler: handleDeleteCard,
      disabled: disabled || isDeleting,
      loading: isDeleting,
      variant: "error",
      condition:
        !availabilityPending &&
        !hasOptimisticCopyIds &&
        !hasReservedCopies &&
        !hasUnresolvedCopyIds &&
        card.ids.length > 0,
    },
  ];

  return useCardActions({ actionDefinitions, disabled });
}
